/**
 * Self-test suite for the about / emoji-status validation, expiry
 * rendering, and owner-side builder.
 *
 * Five groups — mirrors ProfileStatusSelfTest.swift:
 *
 *   A. About validation (~6): trim + length-cap; exact boundary
 *      acceptance (130 scalars); oversize rejection (131 scalars);
 *      empty-after-trim; multi-scalar ZWJ cap counted by scalars.
 *
 *   B. Emoji validation (~5): single emoji-presentation scalar accepted;
 *      non-emoji single char rejected; empty rejected; 2-scalar VS16 form
 *      rejected; ZWJ family rejected (the ZWJ-text-injection vector).
 *
 *   C. Expiry render (~7): expired emoji renders as null; unexpired emoji
 *      renders; null expiry never expires; valid about passes through;
 *      invalid about renders as null (defensive revalidation);
 *      statusExpiresAt does not affect about; isStatusExpired agrees with
 *      renderStatus.
 *
 *   D. Builder (~5): full update produces updated blob; null about clears;
 *      whitespace-only about treated as clear; invalid emoji returns error;
 *      touch-none preserves name/avatarHash but clears status fields.
 *
 *   E. Hardening invariants (~3): expiry uses LOCAL now (not peer-claimed
 *      timestamp); renderer revalidates peer-supplied invalid data; builder
 *      preserves name + avatarHash + schema.
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * Auto-discovered by Vite `import.meta.glob("../**\/*SelfTest.ts")`.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  validateAbout,
  validateEmojiStatus,
  ABOUT_MAX_UNICODE_SCALARS,
} from "./ProfileStatusValidation";
import { renderStatus, isStatusExpired, renderAbout } from "./ProfileStatusExpiry";
import { updateStatus } from "./ProfileStatusBuilder";
import type { ProfileBlob } from "./ProfileBlob";
import { PROFILE_BLOB_CURRENT_SCHEMA } from "./ProfileBlob";

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Profile about / emoji status (validation + expiry)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // A. About validation
      testAboutValidTrimmed(),
      testAboutTrimsWhitespace(),
      testAboutEmptyAfterTrim(),
      testAboutExactly130Allowed(),
      testAbout131Rejected(),
      testAboutMultiScalarCounted(),

      // B. Emoji validation
      testEmojiSingleScalarOk(),
      testEmojiNonEmojiCharRejected(),
      testEmojiEmptyRejected(),
      testEmojiTwoScalarVS16Rejected(),
      testEmojiZWJFamilyRejected(),

      // C. Expiry render
      testRenderExpiredEmojiIsNull(),
      testRenderUnexpiredEmojiRenders(),
      testRenderNullExpiryNeverExpires(),
      testRenderValidAboutPassesThrough(),
      testRenderInvalidAboutIsNull(),
      testRenderExpiryDoesNotAffectAbout(),
      testIsStatusExpiredMatchesRender(),

      // D. Builder
      testBuilderFullUpdate(),
      testBuilderNullAboutClears(),
      testBuilderEmptyAboutTreatedAsClear(),
      testBuilderInvalidEmojiReturnsError(),
      testBuilderTouchNoneClearsStatusFields(),

      // E. Hardening invariants
      testExpiryUsesLocalNow(),
      testRendererRevalidatesPeerData(),
      testBuilderPreservesNameAndAvatar(),
    ]),
});

// ---------------------------------------------------------------------------
// A. About validation
// ---------------------------------------------------------------------------

function testAboutValidTrimmed(): TestCase {
  const r = validateAbout("hello world");
  if (!r.ok) return fail("about: valid short text returns trimmed", `error: ${r.error.kind}`);
  const ok = r.value === "hello world";
  return ok
    ? pass("about: valid short text returns trimmed")
    : fail("about: valid short text returns trimmed", `got "${r.value}"`);
}

function testAboutTrimsWhitespace(): TestCase {
  const r = validateAbout("   trimmed me \n");
  if (!r.ok) return fail("about: leading/trailing whitespace trimmed", `error: ${r.error.kind}`);
  const ok = r.value === "trimmed me";
  return ok
    ? pass("about: leading/trailing whitespace trimmed")
    : fail("about: leading/trailing whitespace trimmed", `got "${r.value}"`);
}

function testAboutEmptyAfterTrim(): TestCase {
  const r = validateAbout("    \n\t ");
  if (r.ok) {
    return fail("about: empty-after-trim returns emptyAfterTrim error", "did not return error");
  }
  return r.error.kind === "emptyAfterTrim"
    ? pass("about: empty-after-trim returns emptyAfterTrim error")
    : fail(
        "about: empty-after-trim returns emptyAfterTrim error",
        `wrong error: ${r.error.kind}`,
      );
}

function testAboutExactly130Allowed(): TestCase {
  const s = "x".repeat(ABOUT_MAX_UNICODE_SCALARS);
  const r = validateAbout(s);
  if (!r.ok) {
    return fail("about: exactly 130 unicode scalars accepted", `error: ${r.error.kind}`);
  }
  const count = [...r.value].length;
  return count === 130
    ? pass("about: exactly 130 unicode scalars accepted")
    : fail("about: exactly 130 unicode scalars accepted", `got count=${count}`);
}

function testAbout131Rejected(): TestCase {
  const s = "x".repeat(131);
  const r = validateAbout(s);
  if (r.ok) {
    return fail("about: 131 unicode scalars returns tooLong error", "did not return error");
  }
  if (r.error.kind === "tooLong" && r.error.unicodeScalarCount === 131) {
    return pass("about: 131 unicode scalars returns tooLong error");
  }
  return fail(
    "about: 131 unicode scalars returns tooLong error",
    `wrong error: ${JSON.stringify(r.error)}`,
  );
}

function testAboutMultiScalarCounted(): TestCase {
  // A ZWJ family emoji is 5 scalars (U+1F468 + U+200D + U+1F469 + U+200D + U+1F467).
  // 27 copies = 135 scalars. `.length` would give 54 UTF-16 code units per cluster
  // × 27 = 1458, but `[...str].length` gives 135.
  // The cap counts scalars, so this must be rejected.
  const family = "\u{1F468}\u{200D}\u{1F469}\u{200D}\u{1F467}"; // 5 scalars
  const s = family.repeat(27);
  const expectedScalarCount = [...s].length;
  const r = validateAbout(s);
  if (r.ok) {
    return fail(
      "about: multi-scalar ZWJ text counted by scalars not graphemes",
      `did not return error (scalarCount=${expectedScalarCount})`,
    );
  }
  if (r.error.kind === "tooLong" && r.error.unicodeScalarCount === expectedScalarCount) {
    return pass("about: multi-scalar ZWJ text counted by scalars not graphemes");
  }
  return fail(
    "about: multi-scalar ZWJ text counted by scalars not graphemes",
    `wrong error: ${JSON.stringify(r.error)}`,
  );
}

// ---------------------------------------------------------------------------
// B. Emoji validation
// ---------------------------------------------------------------------------

function testEmojiSingleScalarOk(): TestCase {
  // U+1F600 GRINNING FACE — single scalar, Emoji_Presentation = true
  const r = validateEmojiStatus("😀");
  if (!r.ok) {
    return fail("emoji: single emoji-presentation scalar accepted", `error: ${r.error.kind}`);
  }
  return r.value === "😀"
    ? pass("emoji: single emoji-presentation scalar accepted")
    : fail("emoji: single emoji-presentation scalar accepted", `got "${r.value}"`);
}

function testEmojiNonEmojiCharRejected(): TestCase {
  // "A" is a single scalar but Emoji_Presentation is false.
  const r = validateEmojiStatus("A");
  if (r.ok) {
    return fail("emoji: non-emoji single char returns notAnEmojiScalar", "did not return error");
  }
  return r.error.kind === "notAnEmojiScalar"
    ? pass("emoji: non-emoji single char returns notAnEmojiScalar")
    : fail(
        "emoji: non-emoji single char returns notAnEmojiScalar",
        `wrong error: ${r.error.kind}`,
      );
}

function testEmojiEmptyRejected(): TestCase {
  const r = validateEmojiStatus("   ");
  if (r.ok) {
    return fail("emoji: empty-after-trim returns empty error", "did not return error");
  }
  return r.error.kind === "empty"
    ? pass("emoji: empty-after-trim returns empty error")
    : fail("emoji: empty-after-trim returns empty error", `wrong error: ${r.error.kind}`);
}

function testEmojiTwoScalarVS16Rejected(): TestCase {
  // U+2764 HEAVY BLACK HEART + U+FE0F VARIATION SELECTOR-16 → "❤️", 2 scalars.
  const heart = "\u{2764}\u{FE0F}";
  const r = validateEmojiStatus(heart);
  if (r.ok) {
    return fail("emoji: 2-scalar VS16 form returns multipleScalars", "did not return error");
  }
  if (r.error.kind === "multipleScalars" && r.error.count === 2) {
    return pass("emoji: 2-scalar VS16 form returns multipleScalars");
  }
  return fail(
    "emoji: 2-scalar VS16 form returns multipleScalars",
    `wrong error: ${JSON.stringify(r.error)}`,
  );
}

function testEmojiZWJFamilyRejected(): TestCase {
  // U+1F468 MAN + U+200D ZWJ + U+1F469 WOMAN + U+200D ZWJ + U+1F467 GIRL — 5 scalars.
  // This is the canonical ZWJ-text-injection attack vector: 5 scalars disguised as
  // "one emoji" in grapheme-cluster-counting APIs.
  const family = "\u{1F468}\u{200D}\u{1F469}\u{200D}\u{1F467}";
  const r = validateEmojiStatus(family);
  if (r.ok) {
    return fail(
      "emoji: ZWJ family sequence returns multipleScalars (ZWJ-text-injection rejected)",
      "did not return error",
    );
  }
  if (r.error.kind === "multipleScalars" && r.error.count === 5) {
    return pass(
      "emoji: ZWJ family sequence returns multipleScalars (ZWJ-text-injection rejected)",
    );
  }
  return fail(
    "emoji: ZWJ family sequence returns multipleScalars (ZWJ-text-injection rejected)",
    `wrong error: ${JSON.stringify(r.error)}`,
  );
}

// ---------------------------------------------------------------------------
// C. Expiry render
// ---------------------------------------------------------------------------

const NOW_UNIX = 1_700_000_000; // fixed reference point (seconds)

function testRenderExpiredEmojiIsNull(): TestCase {
  const blob: ProfileBlob = { name: "alice", emojiStatus: "😀", statusExpiresAt: NOW_UNIX - 1 };
  const v = renderStatus(blob, NOW_UNIX);
  return v.emojiStatus === null
    ? pass("render: expired emoji renders as null")
    : fail("render: expired emoji renders as null", `got "${v.emojiStatus}"`);
}

function testRenderUnexpiredEmojiRenders(): TestCase {
  const blob: ProfileBlob = { name: "alice", emojiStatus: "😀", statusExpiresAt: NOW_UNIX + 60 };
  const v = renderStatus(blob, NOW_UNIX);
  return v.emojiStatus === "😀"
    ? pass("render: unexpired emoji renders")
    : fail("render: unexpired emoji renders", `got "${v.emojiStatus}"`);
}

function testRenderNullExpiryNeverExpires(): TestCase {
  const veryFarFuture = 9_999_999_999;
  const blob: ProfileBlob = { name: "alice", emojiStatus: "😀", statusExpiresAt: null };
  const v = renderStatus(blob, veryFarFuture);
  return v.emojiStatus === "😀"
    ? pass("render: null expiry never expires")
    : fail("render: null expiry never expires", `got "${v.emojiStatus}"`);
}

function testRenderValidAboutPassesThrough(): TestCase {
  const blob: ProfileBlob = { name: "alice", about: "hello world" };
  const v = renderStatus(blob, NOW_UNIX);
  return v.about === "hello world"
    ? pass("render: valid about passes through")
    : fail("render: valid about passes through", `got "${v.about}"`);
}

function testRenderInvalidAboutIsNull(): TestCase {
  // 131 scalars — peer-supplied oversize about; renderer re-validates and
  // surfaces null rather than the garbage payload.
  const blob: ProfileBlob = { name: "alice", about: "x".repeat(131) };
  const v = renderStatus(blob, NOW_UNIX);
  return v.about === null
    ? pass("render: invalid about (oversize) renders as null (defensive revalidation)")
    : fail(
        "render: invalid about (oversize) renders as null (defensive revalidation)",
        `got "${v.about}"`,
      );
}

function testRenderExpiryDoesNotAffectAbout(): TestCase {
  // statusExpiresAt is in the past → emoji should be null, about should be unaffected.
  const blob: ProfileBlob = {
    name: "alice",
    about: "still here",
    emojiStatus: "😀",
    statusExpiresAt: NOW_UNIX - 1,
  };
  const v = renderStatus(blob, NOW_UNIX);
  const ok = v.about === "still here" && v.emojiStatus === null;
  return ok
    ? pass("render: statusExpiresAt does not affect about")
    : fail(
        "render: statusExpiresAt does not affect about",
        `about="${v.about}" emoji="${v.emojiStatus}"`,
      );
}

function testIsStatusExpiredMatchesRender(): TestCase {
  const expiredBlob: ProfileBlob = { name: "a", emojiStatus: "😀", statusExpiresAt: NOW_UNIX - 1 };
  const liveBlob: ProfileBlob = { name: "a", emojiStatus: "😀", statusExpiresAt: NOW_UNIX + 1 };
  const nilBlob: ProfileBlob = { name: "a", emojiStatus: "😀", statusExpiresAt: null };

  const expiredIsExpired = isStatusExpired(expiredBlob, NOW_UNIX) === true;
  const liveIsNotExpired = isStatusExpired(liveBlob, NOW_UNIX) === false;
  const nilIsNotExpired = isStatusExpired(nilBlob, NOW_UNIX) === false;
  const expiredRendersNull = renderStatus(expiredBlob, NOW_UNIX).emojiStatus === null;
  const liveRendersEmoji = renderStatus(liveBlob, NOW_UNIX).emojiStatus === "😀";
  const nilRendersEmoji = renderStatus(nilBlob, NOW_UNIX).emojiStatus === "😀";

  const ok =
    expiredIsExpired &&
    liveIsNotExpired &&
    nilIsNotExpired &&
    expiredRendersNull &&
    liveRendersEmoji &&
    nilRendersEmoji;

  return ok
    ? pass("render: isStatusExpired and renderStatus agree")
    : fail("render: isStatusExpired and renderStatus agree", [
        !expiredIsExpired && "expiredIsExpired=false",
        !liveIsNotExpired && "liveIsNotExpired=false",
        !nilIsNotExpired && "nilIsNotExpired=false",
        !expiredRendersNull && "expiredRendersNull=false",
        !liveRendersEmoji && "liveRendersEmoji=false",
        !nilRendersEmoji && "nilRendersEmoji=false",
      ].filter(Boolean).join("; "));
}

// ---------------------------------------------------------------------------
// D. Builder
// ---------------------------------------------------------------------------

function testBuilderFullUpdate(): TestCase {
  const base: ProfileBlob = { name: "alice", avatarHash: "0102", schema: 1 };
  const result = updateStatus(base, {
    about: "I love crypto",
    emojiStatus: "😀",
    statusExpiresAt: 1_700_000_000,
  });
  if (!result.ok) {
    return fail("builder: full update returns updated blob", `error: ${JSON.stringify(result.error)}`);
  }
  const b = result.blob;
  const ok =
    b.about === "I love crypto" &&
    b.emojiStatus === "😀" &&
    b.statusExpiresAt === 1_700_000_000 &&
    b.name === "alice" &&
    b.avatarHash === "0102";
  return ok
    ? pass("builder: full update returns updated blob")
    : fail("builder: full update returns updated blob", JSON.stringify(b));
}

function testBuilderNullAboutClears(): TestCase {
  const base: ProfileBlob = { name: "alice", about: "old about" };
  const result = updateStatus(base, { about: null });
  if (!result.ok) {
    return fail("builder: null about clears existing about", `error: ${JSON.stringify(result.error)}`);
  }
  const ok = result.blob.about == null && result.blob.name === "alice";
  return ok
    ? pass("builder: null about clears existing about")
    : fail("builder: null about clears existing about", `about=${result.blob.about}`);
}

function testBuilderEmptyAboutTreatedAsClear(): TestCase {
  const base: ProfileBlob = { name: "alice", about: "old about" };
  const result = updateStatus(base, { about: "   " });
  if (!result.ok) {
    return fail("builder: whitespace-only about treated as clear", `error: ${JSON.stringify(result.error)}`);
  }
  const ok = result.blob.about == null;
  return ok
    ? pass("builder: whitespace-only about treated as clear")
    : fail("builder: whitespace-only about treated as clear", `about=${result.blob.about}`);
}

function testBuilderInvalidEmojiReturnsError(): TestCase {
  const base: ProfileBlob = { name: "alice" };
  const result = updateStatus(base, { emojiStatus: "A" });
  if (result.ok) {
    return fail("builder: invalid emoji propagates as error", "did not return error");
  }
  if (
    result.error.kind === "invalidEmoji" &&
    result.error.error.kind === "notAnEmojiScalar"
  ) {
    return pass("builder: invalid emoji propagates as error");
  }
  return fail(
    "builder: invalid emoji propagates as error",
    `wrong error: ${JSON.stringify(result.error)}`,
  );
}

function testBuilderTouchNoneClearsStatusFields(): TestCase {
  const base: ProfileBlob = {
    name: "alice",
    avatarHash: "55",
    about: "old",
    emojiStatus: "😀",
    statusExpiresAt: 1_700_000_000,
  };
  const result = updateStatus(base, { about: null, emojiStatus: null, statusExpiresAt: null });
  if (!result.ok) {
    return fail("builder: touch-none clears status, preserves name/avatar", `error: ${JSON.stringify(result.error)}`);
  }
  const b = result.blob;
  const ok =
    b.about == null &&
    b.emojiStatus == null &&
    b.statusExpiresAt == null &&
    b.name === "alice" &&
    b.avatarHash === "55";
  return ok
    ? pass("builder: touch-none clears status, preserves name/avatar")
    : fail("builder: touch-none clears status, preserves name/avatar", JSON.stringify(b));
}

// ---------------------------------------------------------------------------
// E. Hardening invariants
// ---------------------------------------------------------------------------

function testExpiryUsesLocalNow(): TestCase {
  // A server might replay an old blob whose `statusExpiresAt` is "in the
  // future" by peer-claimed time. The renderer always consults the LOCAL `now`
  // we pass in, so passing a `now` past the expiry yields null.
  const peerClaimedExpiry = 1_700_000_000;
  const localNowPastExpiry = peerClaimedExpiry + 10; // local clock advanced past it
  const blob: ProfileBlob = { name: "alice", emojiStatus: "😀", statusExpiresAt: peerClaimedExpiry };
  const v = renderStatus(blob, localNowPastExpiry);
  return v.emojiStatus === null
    ? pass("hardening: expiry uses LOCAL now, not peer-claimed timestamp")
    : fail(
        "hardening: expiry uses LOCAL now, not peer-claimed timestamp",
        `got "${v.emojiStatus}"`,
      );
}

function testRendererRevalidatesPeerData(): TestCase {
  // Peer-supplied blob with both fields malformed: about=oversize, emojiStatus="A".
  // Renderer must surface null for both rather than garbage.
  const blob: ProfileBlob = {
    name: "alice",
    about: "x".repeat(131),
    emojiStatus: "A",
    statusExpiresAt: null,
  };
  const v = renderStatus(blob, NOW_UNIX);
  const ok = v.about === null && v.emojiStatus === null;
  return ok
    ? pass("hardening: renderer revalidates peer-supplied invalid data")
    : fail(
        "hardening: renderer revalidates peer-supplied invalid data",
        `about="${v.about}" emoji="${v.emojiStatus}"`,
      );
}

function testBuilderPreservesNameAndAvatar(): TestCase {
  const base: ProfileBlob = { name: "alice", avatarHash: "abcd", schema: PROFILE_BLOB_CURRENT_SCHEMA };
  const result = updateStatus(base, {
    about: "new about",
    emojiStatus: "😀",
    statusExpiresAt: 12345,
  });
  if (!result.ok) {
    return fail("hardening: builder preserves name + avatarHash + schema", `error: ${JSON.stringify(result.error)}`);
  }
  const b = result.blob;
  const ok =
    b.name === "alice" &&
    b.avatarHash === "abcd" &&
    b.schema === PROFILE_BLOB_CURRENT_SCHEMA;
  return ok
    ? pass("hardening: builder preserves name + avatarHash + schema")
    : fail(
        "hardening: builder preserves name + avatarHash + schema",
        `name="${b.name}" avatarHash="${b.avatarHash}" schema=${b.schema}`,
      );
}
